/**
 * Resolve a partial option selection to a real variant.
 *
 * Le Parfait products pair a Size with a "Set of" count, and only some pairings
 * exist: Swing Top bottles come in 3s, Milk and Juice caps in 6s. Horizon leaves
 * an impossible pairing unresolved. The hidden `input[name="id"]` goes empty, Add
 * to Cart has nothing to post, and vSlide never sees the variant change so the
 * gallery keeps the previous size's images. Debut moved the second option to the
 * first valid value, so buyers never met that state.
 *
 * This restores that behaviour. It listens in the capture phase, before Horizon's
 * own change handler on <variant-picker>, so the corrected selection is already in
 * the DOM by the time Horizon builds its section request. Everything downstream —
 * price, availability, gallery, URL, quick-add bar, Add to Cart — comes from that
 * one request, so they move together.
 */

const MATRIX_SELECTOR = 'script[data-lp-variant-matrix]';

/**
 * @typedef {{ id: number, available: boolean, options: string[] }} LpVariant
 * @typedef {{ names: string[], variants: LpVariant[] }} LpMatrix
 */

/** @type {WeakMap<Element, LpMatrix | null>} */
const matrixCache = new WeakMap();

/**
 * @param {Element} picker
 * @returns {LpMatrix | null}
 */
function readMatrix(picker) {
  if (matrixCache.has(picker)) return matrixCache.get(picker) ?? null;

  const script = picker.querySelector(MATRIX_SELECTOR);
  let matrix = null;

  if (script?.textContent) {
    try {
      const parsed = JSON.parse(script.textContent);
      if (Array.isArray(parsed?.names) && Array.isArray(parsed?.variants) && parsed.names.length > 1) {
        matrix = parsed;
      }
    } catch {
      matrix = null;
    }
  }

  matrixCache.set(picker, matrix);
  return matrix;
}

/**
 * The option values currently checked, in the product's own option order.
 * @param {Element} picker
 * @param {string[]} names
 * @returns {(string | undefined)[]}
 */
function currentSelection(picker, names) {
  const selection = new Array(names.length);

  for (const select of picker.querySelectorAll('select')) {
    const option = select.selectedOptions[0];
    const index = names.indexOf(option?.dataset.optionName ?? '');
    if (index !== -1) selection[index] = select.value;
  }

  for (const input of picker.querySelectorAll('fieldset input:checked')) {
    const index = names.indexOf(input.dataset.optionName ?? '');
    if (index !== -1) selection[index] = input.value;
  }

  return selection;
}

/**
 * The variant matching every selected value, if there is one.
 * @param {LpMatrix} matrix
 * @param {(string | undefined)[]} selection
 * @returns {LpVariant | undefined}
 */
function exactMatch(matrix, selection) {
  return matrix.variants.find((variant) => selection.every((value, index) => variant.options[index] === value));
}

/**
 * The closest variant that keeps the value the buyer just picked.
 *
 * Earlier options are worth more than later ones, so choosing a Size keeps that
 * Size and moves "Set of", never the other way round. An in-stock combination
 * always beats a sold-out one, so we never silently land the buyer on a variant
 * they cannot buy while a buyable pairing exists.
 *
 * @param {LpMatrix} matrix
 * @param {(string | undefined)[]} selection
 * @param {number} changedIndex
 * @returns {LpVariant | undefined}
 */
function nearestVariant(matrix, selection, changedIndex) {
  const changedValue = selection[changedIndex];
  let best;
  let bestScore = -1;

  matrix.variants.forEach((variant) => {
    if (variant.options[changedIndex] !== changedValue) return;

    let score = variant.available ? 1_000_000 : 0;

    selection.forEach((value, index) => {
      if (index === changedIndex || value === undefined) return;
      if (variant.options[index] === value) score += 1 << (selection.length - index);
    });

    if (score > bestScore) {
      bestScore = score;
      best = variant;
    }
  });

  return best;
}

/**
 * Check the control holding one option value, keeping Horizon's own selection
 * bookkeeping in step so its button styling and transitions stay correct.
 * @param {Element} picker
 * @param {string} name
 * @param {string} value
 */
function selectOptionValue(picker, name, value) {
  const escapedName = CSS.escape(name);

  const input = picker.querySelector(`fieldset input[data-option-name="${escapedName}"][value="${CSS.escape(value)}"]`);
  if (input instanceof HTMLInputElement) {
    if (typeof picker.updateSelectedOption === 'function') {
      picker.updateSelectedOption(input);
    } else {
      input.checked = true;
    }
    return;
  }

  for (const select of picker.querySelectorAll('select')) {
    const option = Array.from(select.options).find(
      (candidate) => candidate.dataset.optionName === name && candidate.value === value
    );
    if (!option) continue;
    for (const other of select.options) other.removeAttribute('selected');
    option.setAttribute('selected', 'selected');
    select.value = value;
    return;
  }
}

/**
 * @param {Event} event
 */
function onChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

  const picker = target.closest('variant-picker');
  if (!picker) return;

  const matrix = readMatrix(picker);
  if (!matrix) return;

  const { names } = matrix;
  const changedName =
    target instanceof HTMLSelectElement ? target.selectedOptions[0]?.dataset.optionName : target.dataset.optionName;
  const changedIndex = names.indexOf(changedName ?? '');
  if (changedIndex === -1) return;

  const selection = currentSelection(picker, names);
  if (exactMatch(matrix, selection)) return;

  const variant = nearestVariant(matrix, selection, changedIndex);
  if (!variant) return;

  names.forEach((name, index) => {
    if (index === changedIndex || variant.options[index] === selection[index]) return;
    selectOptionValue(picker, name, variant.options[index]);
  });

  // Horizon reads this to write ?variant= into the address bar, which is also how
  // vSlide learns the gallery has to change.
  target.dataset.variantId = String(variant.id);
}

document.addEventListener('change', onChange, true);
